import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import useUpdateViewerPageState from '../useUpdateViewerPageState';
import {
  v2ScrollTargetPageAtom,
  v2ViewModeAtom,
} from '../../store/v2/viewer.atom';

interface Props {
  totalPages: number;
  ctaFormPage?: number;
  /** startFrom 게이트가 활성(미제출) 상태인지 */
  isGateActive: boolean;
}

/**
 * V2 공통 페이지 이동 로직.
 * - 1..totalPages 클램프
 * - startFrom 게이트 활성 시 게이트 페이지 너머 이동을 게이트 페이지로 강제
 * - 스크롤 모드에서는 v2ScrollTargetPageAtom으로 스크롤 이동을 요청
 * 모든 이동이 updateCurrentPage를 경유하므로 체류시간 분석 계약이 유지된다.
 */
const useV2PageNavigation = ({
  totalPages,
  ctaFormPage,
  isGateActive,
}: Props) => {
  const { currentPage, updateCurrentPage } = useUpdateViewerPageState();
  const viewMode = useAtomValue(v2ViewModeAtom);
  const setScrollTargetPage = useSetAtom(v2ScrollTargetPageAtom);

  const isNextPageDisabled =
    isGateActive && ctaFormPage !== undefined && currentPage >= ctaFormPage;

  const goToPage = useCallback(
    (targetPage: number) => {
      let page = Math.min(Math.max(Math.round(targetPage), 1), totalPages);

      if (isGateActive && ctaFormPage !== undefined && page >= ctaFormPage) {
        page = ctaFormPage;
      }

      if (page === currentPage) {
        return;
      }

      updateCurrentPage(page);

      if (viewMode === 'scroll') {
        setScrollTargetPage(page);
      }
    },
    [
      totalPages,
      isGateActive,
      ctaFormPage,
      currentPage,
      updateCurrentPage,
      viewMode,
      setScrollTargetPage,
    ],
  );

  const goToPrevPage = useCallback(
    () => goToPage(currentPage - 1),
    [goToPage, currentPage],
  );

  const goToNextPage = useCallback(() => {
    if (isNextPageDisabled) {
      return;
    }
    goToPage(currentPage + 1);
  }, [goToPage, currentPage, isNextPageDisabled]);

  return {
    currentPage,
    goToPage,
    goToPrevPage,
    goToNextPage,
    isNextPageDisabled,
  };
};

export default useV2PageNavigation;
